import { useState } from 'react';
import type { CreateUserInput, RoleCode } from '@wave/shared';
import { useCreateUser, useDeleteUser, useUpdateUser, useUsers } from '../api/users';
import { generatePassword } from '../lib/password';
import {
  btnGhostSm,
  btnPrimary,
  btnPrimarySm,
  fieldLabelCls,
  inputCls,
  pageContainer,
  pageHeader,
  pageTitle,
  sectionTitle,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from '../lib/styles';

const ROLE_OPTIONS: RoleCode[] = ['admin', 'manager', 'operator'];

export function AdminUsersPage() {
  const { data: users, isLoading, error } = useUsers();
  const create = useCreateUser();
  const update = useUpdateUser();
  const del = useDeleteUser();
  const [showForm, setShowForm] = useState(false);
  // Сброс пароля пользователю: сгенерированный пароль показывается один раз
  // до «Применить» — после сохранения восстановить его нельзя (хранится bcrypt-хеш).
  const [pwReset, setPwReset] = useState<{ id: string; email: string; password: string } | null>(null);
  const [pwApplied, setPwApplied] = useState(false);

  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <h1 className={pageTitle}>Пользователи</h1>
        <button onClick={() => setShowForm((v) => !v)} className={btnPrimarySm}>
          {showForm ? 'Закрыть форму' : '+ Новый пользователь'}
        </button>
      </div>

      {showForm && (
        <CreateForm
          onCreate={async (input) => {
            await create.mutateAsync(input);
            setShowForm(false);
          }}
          isPending={create.isPending}
          error={create.error ? (create.error as Error).message : null}
        />
      )}

      {pwReset && (
        <div className="mb-4 rounded-lg border border-[var(--accent-border)] bg-[var(--accent-bg)] p-4">
          <div className="mb-2 text-sm font-medium text-text">
            Новый пароль для <span className="font-mono">{pwReset.email}</span>
          </div>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 rounded-md border border-border bg-bg px-3 py-1.5 font-mono text-sm text-text">
              {pwReset.password}
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(pwReset.password)}
              className={btnGhostSm}
              title="Скопировать"
            >
              ⧉
            </button>
            <button
              type="button"
              onClick={() => setPwReset({ ...pwReset, password: generatePassword() })}
              className={btnGhostSm}
              title="Сгенерировать другой"
            >
              🎲
            </button>
          </div>
          <div className="mt-2 text-[11px] text-text2">
            Скопируй пароль сейчас — после применения он больше нигде не отобразится.
          </div>
          <div className="mt-3 flex gap-2">
            {!pwApplied ? (
              <>
                <button
                  type="button"
                  disabled={update.isPending}
                  onClick={() =>
                    void update
                      .mutateAsync({ id: pwReset.id, patch: { password: pwReset.password } })
                      .then(() => setPwApplied(true))
                  }
                  className={btnPrimarySm}
                >
                  {update.isPending ? 'Сохранение…' : 'Применить'}
                </button>
                <button
                  type="button"
                  onClick={() => setPwReset(null)}
                  className={btnGhostSm}
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                <span className="text-sm font-medium text-success">✓ Пароль изменён</span>
                <button
                  type="button"
                  onClick={() => {
                    setPwReset(null);
                    setPwApplied(false);
                  }}
                  className={btnGhostSm}
                >
                  Закрыть
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {isLoading && <div className="text-sm text-text2">Загрузка…</div>}
      {error && (
        <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {users && (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Email</th>
                <th className={tableHeadCell}>Имя</th>
                <th className={tableHeadCell}>Telegram</th>
                <th className={tableHeadCell}>Роли</th>
                <th className={tableHeadCell}>Активен</th>
                <th className={`${tableHeadCell} text-right`}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={tableRow}>
                  <td className={`${tableCell} font-mono text-text`}>{u.email}</td>
                  <td className={tableCell}>{u.displayName}</td>
                  <td className={`${tableCell} text-text2`}>{u.telegramUsername ?? '—'}</td>
                  <td className={tableCell}>
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <span
                          key={r}
                          className="inline-flex items-center rounded border border-[var(--accent-border)] bg-[var(--accent-bg)] px-1.5 py-0.5 text-[11px] font-medium text-accent"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className={tableCell}>
                    {u.isActive ? (
                      <span className="text-success">✓</span>
                    ) : (
                      <span className="text-text2">✗</span>
                    )}
                  </td>
                  <td className={`${tableCell} text-right`}>
                    <button
                      onClick={() => {
                        setPwApplied(false);
                        setPwReset({ id: u.id, email: u.email, password: generatePassword() });
                      }}
                      className="mr-3 text-xs font-medium text-text2 underline-offset-4 transition-colors duration-150 hover:text-text hover:underline"
                    >
                      Сбросить пароль
                    </button>
                    <button
                      onClick={() =>
                        void update.mutateAsync({
                          id: u.id,
                          patch: { isActive: !u.isActive },
                        })
                      }
                      className="mr-3 text-xs font-medium text-text2 underline-offset-4 transition-colors duration-150 hover:text-text hover:underline"
                    >
                      {u.isActive ? 'Деактивировать' : 'Активировать'}
                    </button>
                    {u.isActive && (
                      <button
                        onClick={() => {
                          if (confirm(`Деактивировать ${u.email}?`)) void del.mutateAsync(u.id);
                        }}
                        className="text-xs font-medium text-danger underline-offset-4 transition-colors duration-150 hover:underline"
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface CreateFormProps {
  onCreate: (input: CreateUserInput) => Promise<void>;
  isPending: boolean;
  error: string | null;
}

function CreateForm({ onCreate, isPending, error }: CreateFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [roles, setRoles] = useState<RoleCode[]>(['operator']);

  function toggleRole(r: RoleCode) {
    setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (roles.length === 0) return;
    void onCreate({
      email,
      password,
      displayName,
      telegramUsername: telegramUsername || null,
      roles,
    });
  }

  return (
    <form onSubmit={submit} className="mb-6 rounded-lg border border-border bg-bg2 p-5">
      <h2 className={`${sectionTitle} mb-4`}>Новый пользователь</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Пароль">
          <div className="flex items-stretch gap-2">
            <input
              type={showPassword ? 'text' : 'password'}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputCls} font-mono`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className={btnGhostSm}
              title={showPassword ? 'Скрыть' : 'Показать'}
            >
              {showPassword ? '🙈' : '👁'}
            </button>
            <button
              type="button"
              onClick={() => {
                setPassword(generatePassword());
                setShowPassword(true);
              }}
              className={btnGhostSm}
              title="Сгенерировать надёжный пароль"
            >
              🎲
            </button>
          </div>
        </Field>
        <Field label="Имя">
          <input
            type="text"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Telegram username (опционально)">
          <input
            type="text"
            placeholder="@username"
            value={telegramUsername}
            onChange={(e) => setTelegramUsername(e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>
      <div className="mt-4">
        <span className={fieldLabelCls}>Роли</span>
        <div className="flex flex-wrap gap-2">
          {ROLE_OPTIONS.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 cursor-pointer ${
                roles.includes(r)
                  ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
                  : 'border-border bg-bg text-text2 hover:bg-bg3'
              }`}
            >
              <input
                type="checkbox"
                checked={roles.includes(r)}
                onChange={() => toggleRole(r)}
                className="h-3.5 w-3.5 accent-accent"
              />
              {r}
            </label>
          ))}
        </div>
      </div>
      {error && (
        <div className="mt-4 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      <div className="mt-5 flex justify-end border-t border-border-subtle pt-4">
        <button type="submit" disabled={isPending} className={btnPrimary}>
          {isPending ? 'Создание…' : 'Создать'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className={fieldLabelCls}>{label}</span>
      {children}
    </label>
  );
}
